// src/data/agreementCustody.js
//
// AGR-3 - THE ADOPTION GATE. Whose agreement is it, who examined it, and may the platform cite it?
// Task list section 361. Designed 2026-09-22 after Bob put the question that this module answers:
// he has collected agreement documents the General Chairmen have not examined, and he asked whether
// the GCs should delete them and install their own to keep him clear of an erroneous rule.
//
// THE ANSWER, AND THE WHOLE DESIGN IN ONE SENTENCE:
//
//   HOLDING A DOCUMENT IS LABOUR; SAYING WHAT THE CONTROLLING AGREEMENT IS, IS AUTHORITY - AND
//   RULINGS 136 AND 145 PUT THAT AUTHORITY WITH THE GENERAL CHAIRPERSON, EXCLUSIVELY.
//
// So nobody deletes anything. A document sits in the library as a REFERENCE COPY, inert: displayed,
// attributed to whoever obtained it and from where, and REFUSED BY THE CITATION GATE. It becomes
// citable when, and only when, its custodian General Chairperson performs an act of adoption that
// binds her name and the date to THAT FILE'S HASH. If she disagrees she files her own, and the first
// is superseded - never deleted, because a claim decided while a document was live must always be
// answerable with the text that was on screen that day.
//
// WHY A HASH AND NOT A FILENAME. She adopts a TEXT, not a title. Without the hash, "she approved the
// agreements folder" is the whole record, and a file replaced afterwards inherits her signature. With
// it, a changed file falls out of adoption by itself and has to be adopted again.
//
// THE THIRD STATE, AND IT IS THE REASON THIS MODULE EXISTS AT ALL. The sweep of 2026-09-22 found 32
// files in `OneDrive\Copilot\m,trak Ym Agt CSVs\` that are MACHINE-WRITTEN DERIVATIVES of the Amtrak
// agreement - a "Complete Modern-Language Rewrite", an "Encyclopedic Master Agreement Book", a
// "Field-Ready Quick Reference Manual". Those are not bad versions of the agreement. THEY ARE NOT
// VERSIONS OF IT AT ALL, and there is nothing in them for a General Chairperson to adopt: every
// sentence was produced by a model that ruling 135 says is not to be trusted, including on the exact
// rule an officer would quote to a carrier. `DERIVED` is a terminal state. It cannot be adopted, it
// cannot be cited, and the intake refuses to file such a document as an agreement in the first place.
//
// PURE. No Firestore, no network, no clock; the caller supplies the time and the hash.

const F = Object.freeze;
const nonEmpty = (v) => typeof v === 'string' && v.trim().length > 0;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ------------------------------------------------------------------------------------------------
// THE STATES
// ------------------------------------------------------------------------------------------------

export const REFERENCE = 'reference_only';
export const ADOPTED = 'adopted';
export const SUPERSEDED = 'superseded';
export const DERIVED = 'derived_not_an_agreement';
export const STATES = F([REFERENCE, ADOPTED, SUPERSEDED, DERIVED]);

// What a reader is told, in each state, in words rather than a badge. The platform never shows a
// state name alone: a member who reads "reference_only" has learned nothing.
export const STATE_TEXT = F({
  [REFERENCE]: 'REFERENCE COPY. Obtained and held for this committee, but NOT examined or adopted by '
    + 'the General Chairperson. It is not the controlling agreement and nothing here is cited from it.',
  [ADOPTED]: 'ADOPTED as a controlling agreement of this committee by its General Chairperson, who '
    + 'examined this exact file.',
  [SUPERSEDED]: 'SUPERSEDED by a later document. Kept, because a claim decided while this was in force '
    + 'must still be answerable with the text that was in force.',
  [DERIVED]: 'NOT AN AGREEMENT. This document was produced by summarising, rewriting or restating an '
    + 'agreement. It is never cited, never adopted, and must not be read as the agreement.',
});

export function stateText(state) { return STATE_TEXT[String(state || '')] || ''; }

// THE CITATION GATE - the one question the rest of the platform asks this module.
export function mayCite(doc) {
  const d = doc || {};
  if (String(d.state) !== ADOPTED) {
    return { may: false, because: stateText(d.state) || 'This document has no recorded custody state, '
      + 'so it is treated as unexamined and is not cited.' };
  }
  // Adoption is bound to a hash. If the file has changed since, the adoption does not travel with it.
  if (!nonEmpty(d.adoptedHash) || !nonEmpty(d.contentHash) || d.adoptedHash !== d.contentHash) {
    return { may: false, because: 'This file has changed since it was adopted. The adoption covered '
      + 'the text that was examined, not this one, and it must be adopted again before it is cited.' };
  }
  return { may: true, because: '' };
}

// ------------------------------------------------------------------------------------------------
// INTAKE - what the platform asks for once, because it cannot be recovered later
// ------------------------------------------------------------------------------------------------

// Names that announce a machine-written restatement. Matched on the FILENAME because that is what
// exists at intake, and deliberately generous: a false positive costs one question to the loader, a
// false negative puts an invented rule in front of an officer.
export const DERIVED_MARKERS = F([
  /\brewrite\b/i, /\bmodern[- ]language\b/i, /\bsummary\b/i, /\bsummaris/i, /\bsummariz/i,
  /\bencyclopedic\b/i, /\bquick reference\b/i, /\bfield[- ]ready\b/i, /\bstudy (guide|compilation)\b/i,
  /\banalysis\b/i, /\bexecutive summary\b/i, /\bcheat ?sheet\b/i, /\bplain[- ]english\b/i,
  /\brestat(ed|ement)\b/i, /\bexplained\b/i, /\bhandbook\b/i, /\bmanual\b/i, /\boverview\b/i,
]);

export function looksDerived(fileName) {
  const n = String(fileName || '');
  return DERIVED_MARKERS.some((re) => re.test(n));
}

// Record 589 (audit), ruling 211: the words the LIVE upload path (src\data\agreementsRepo.js uploadVersion /
// createAgreement) throws when looksDerived() catches a file name. Until this, nothing in that path called
// looksDerived, and a "Complete Modern-Language Rewrite" landed status:'active' in the library. Plain words,
// no ruling number in the sentence (ruling 168): the uploader is an officer and reads it in an alert.
export const DERIVED_UPLOAD_REFUSED =
  'Not uploaded. The file name says this is a rewrite, summary, restatement, handbook, manual or other '
  + 'machine-written version of an agreement, and a restatement of an agreement is never the agreement. '
  + 'Upload the agreement text itself. If this file truly is the agreement, rename it so its name does not '
  + 'describe it as a rewrite or summary, and upload it again.';

// Record 599 (Bob, 2026-10-07): RULING 211 ATTESTATION AT UPLOAD. The derived-name refusal above catches a
// restatement by its NAME; this is the uploader's own word that the file IS the agreement. A required tick on
// every agreement upload screen (src\pages\Agreements.js, both paths), stored on the version with who, when and
// these exact words. firestore.rules holds the same sentence, character for character, and refuses a new
// agreement or version without it (admin included). Proved by scripts\test-agreement-attestation.mjs.
export const ATTESTATION_TEXT =
  'This file is the agreement as signed and issued (or the carrier/union official printing). It is not a summary, extract or retyped copy.';

export const ATTESTATION_REQUIRED =
  'Not uploaded. Tick the box to confirm that this file is the agreement as signed and issued (or the '
  + 'carrier/union official printing), and not a summary, extract or retyped copy.';

// The three fields the uploader's tick puts on the record. Time is NOT here: the repo stamps server time
// (attestedAt: serverTimestamp()), which the rules compare to request.time. Only a real `true` is a tick.
export function attestationFields(user, attested) {
  if (attested !== true) return { ok: false, because: ATTESTATION_REQUIRED, fields: null };
  if (!user || !nonEmpty(user.uid)) {
    return { ok: false, because: 'Not uploaded. An agreement is attested under a signed-in account, and none is signed in.', fields: null };
  }
  return {
    ok: true,
    because: '',
    fields: {
      attestedBy: String(user.uid),
      attestedByName: String(user.name || user.email || ''),
      attestationText: ATTESTATION_TEXT,
    },
  };
}

// Personal material must never be filed as an agreement (Bob, 2026-09-22: "between personal finances,
// health issues, and a plethora of other valid reasons, let's protect everyone's privacy"). An
// agreement is a public instrument of a committee; a document about a PERSON is not, and filing one
// here would put it where every officer of that committee can read it.
export const PERSONAL_MARKERS = F([
  /\bmedical\b/i, /\bdiagnos/i, /\bhealth\b/i, /\bfmla\b/i, /\bdisabilit/i,
  /\bpayroll\b/i, /\bW-?2\b/i, /\b1099\b/i, /\bssn\b/i, /\bsocial security\b/i,
  /\bpersonnel file\b/i, /\bdiscipline (letter|record)\b/i, /\binvestigation transcript\b/i,
]);

export function looksPersonal(fileName) {
  const n = String(fileName || '');
  return PERSONAL_MARKERS.some((re) => re.test(n));
}

// The two answers no later process can supply, plus the hash the platform computes for itself.
export function validateIntake(input) {
  const i = input || {};
  const problems = [];
  const name = String(i.fileName || '');

  if (!nonEmpty(name)) problems.push('the file name is required');
  if (!nonEmpty(i.obtainedFrom)) {
    problems.push('say where this document came from - a General Chairperson adopting it later is '
      + 'adopting THIS text obtained HERE, and nobody can reconstruct it afterwards');
  }
  if (!DATE_RE.test(String(i.obtainedOn || ''))) problems.push('the date it was obtained is required, YYYY-MM-DD');
  if (!nonEmpty(i.contentHash)) problems.push('the content hash is required - it is what binds an adoption to a text');
  if (!nonEmpty(i.custodianCommittee)) {
    problems.push('no custodian committee - this document could not be routed on craft, railroad and '
      + 'territory, and it is flagged rather than guessed at');
  }

  const derived = looksDerived(name) || i.derived === true;
  const personal = looksPersonal(name) || i.personal === true;
  if (personal) {
    problems.push('this looks like a document about a PERSON, not an instrument of a committee. It is '
      + 'not filed as an agreement: an agreement is readable by every officer of its committee, and a '
      + "member's own material must never be put there");
  }

  return {
    ok: problems.length === 0 && !derived,
    problems,
    derived,
    personal,
    // A derived document is not an error to be fixed - it is filed, in its own terminal state, so the
    // platform has a record of having seen and refused it.
    state: derived ? DERIVED : REFERENCE,
    because: derived
      ? 'Filed as NOT AN AGREEMENT. This document restates or rewrites an agreement rather than being '
        + 'one, so there is nothing in it for a General Chairperson to adopt. [R-211]'
      : '',
  };
}

export function buildDocument(input, now) {
  const i = input || {};
  const v = validateIntake(i);
  return {
    fileName: String(i.fileName || ''),
    title: String(i.title || i.fileName || ''),
    custodianCommittee: String(i.custodianCommittee || ''),
    scopeKey: String(i.scopeKey || ''),
    contentHash: String(i.contentHash || ''),
    // PROVENANCE - two answers, asked once, at the only moment they exist.
    obtainedFrom: String(i.obtainedFrom || ''),
    obtainedOn: String(i.obtainedOn || ''),
    loadedByUid: String(i.loadedByUid || ''),
    loadedByName: String(i.loadedByName || ''),
    loadedAt: String(now || ''),
    state: v.state,
    // Adoption, empty until the custodian acts.
    adoptedByUid: '', adoptedByName: '', adoptedAt: '', adoptedHash: '',
    supersededBy: '',
    noticedAt: '', noticedTo: '',
  };
}

// ------------------------------------------------------------------------------------------------
// THE ACTS
// ------------------------------------------------------------------------------------------------

// Who may adopt: the General Chairperson of the custodian committee. NOT the administrator - he can
// load, route and notify, and he is exactly the person whose say-so this gate exists to remove from
// the question (rulings 136, 145).
export function mayAdopt(actor, doc) {
  const a = actor || {};
  const d = doc || {};
  if (String(d.state) === DERIVED) {
    return { may: false, because: 'This document restates an agreement rather than being one. There is '
      + 'nothing here to adopt. [R-211]' };
  }
  if (String(d.state) === ADOPTED && d.adoptedHash === d.contentHash) {
    return { may: false, because: 'Already adopted, and the file is unchanged since.' };
  }
  if (String(a.role) !== 'general_chair') {
    return { may: false, because: 'Only the General Chairperson of this committee may say what its '
      + 'controlling agreement is. The administrator may hold a document and tell '
      + 'her it is here; he may not adopt it for her. [R-136] [R-145]' };
  }
  if (String(a.committee || '') !== String(d.custodianCommittee || '') || !nonEmpty(d.custodianCommittee)) {
    return { may: false, because: 'This document belongs to another committee.' };
  }
  return { may: true, because: '' };
}

// The act itself. It binds her name, the date and THE HASH SHE EXAMINED, and it states in her own
// record what she is asserting - because "approved" is not a claim anyone can check later.
export const ADOPTION_DECLARATION =
  'I have examined this document. It is a controlling agreement of this committee, and I am its '
  + 'custodian. I understand that it will be cited by this platform from now on, and that a change to '
  + 'this file removes it from adoption until I examine it again.';

export function adopt(doc, actor, now) {
  const may = mayAdopt(doc && actor ? actor : {}, doc);
  if (!may.may) return { ok: false, because: may.because, patch: null };
  const d = doc || {};
  const a = actor || {};
  if (!nonEmpty(d.contentHash)) {
    return { ok: false, because: 'This document has no content hash, so an adoption could not be bound '
      + 'to a text. It cannot be adopted.', patch: null };
  }
  return {
    ok: true,
    because: '',
    patch: {
      state: ADOPTED,
      adoptedByUid: String(a.uid || ''),
      adoptedByName: String(a.name || ''),
      adoptedAt: String(now || ''),
      adoptedHash: String(d.contentHash),
      declaration: ADOPTION_DECLARATION,
    },
  };
}

// A replacement supersedes rather than deletes.
export function supersede(doc, byDocId, now) {
  const d = doc || {};
  if (!nonEmpty(byDocId)) return { ok: false, because: 'Name the document that replaces it.', patch: null };
  return {
    ok: true, because: '',
    patch: { state: SUPERSEDED, supersededBy: String(byDocId), supersededAt: String(now || '') },
  };
}

// THE NOTICE ACT, and it is the one that protects the man who did the loading. It records that the
// custodian was TOLD, on a date, that these documents are held for her committee and are inert until
// she acts. Without it the record says only that she never looked; with it, the record says she was
// asked, and that the platform refused to treat the documents as controlling in the meantime.
export function noticeOfHolding(docs, committee, now) {
  const list = (Array.isArray(docs) ? docs : []).filter((d) => d && String(d.custodianCommittee) === String(committee));
  const holdings = list.filter((d) => String(d.state) === REFERENCE);
  const derived = list.filter((d) => String(d.state) === DERIVED);
  const lines = [];
  lines.push('DOCUMENTS HELD FOR YOUR COMMITTEE, AND NOT YET EXAMINED BY YOU');
  lines.push('');
  lines.push('Committee: ' + String(committee || '[NOT ON FILE]'));
  lines.push('Date of this notice: ' + (nonEmpty(now) ? String(now) : '[NOT ON FILE]'));
  lines.push('');
  lines.push('These documents were obtained and loaded so that the work of finding them is done. They');
  lines.push('are NOT in use. The platform does not cite them, does not match a rule from them, and does');
  lines.push('not present them as your controlling agreement, because you have not examined them and it');
  lines.push('is not the platform\'s place to say what your agreement is.');
  lines.push('');
  if (holdings.length) {
    holdings.forEach((d) => {
      lines.push('  - ' + d.title + (nonEmpty(d.obtainedFrom) ? '   (obtained from ' + d.obtainedFrom : '   (source not recorded')
        + (nonEmpty(d.obtainedOn) ? ', ' + d.obtainedOn + ')' : ')'));
    });
  } else {
    lines.push('  - None held for this committee.');
  }
  lines.push('');
  lines.push('For each one, three answers are open to you: ADOPT it as a controlling agreement of this');
  lines.push('committee, REPLACE it with your own file, or say it does not belong to this committee.');
  lines.push('Whatever you adopt is recorded in your name and dated. Whatever you do not stays inert.');
  if (derived.length) {
    lines.push('');
    lines.push('Also held, and NOT offered to you, because they are not agreements: ' + derived.length
      + ' machine-written summaries or rewrites. They cannot be adopted or cited by anyone.');
  }
  return {
    text: lines.join('\n'),
    committee: String(committee || ''),
    count: holdings.length,
    derivedCount: derived.length,
    sendable: nonEmpty(committee) && nonEmpty(now),
  };
}

// How long a holding may sit unanswered before the record says so out loud. Same shape as the
// obligation's 60-day flag: the platform does not chase anybody, it just stops the silence being
// invisible.
export const NOTICE_FLAG_DAYS = 30;

export function noticeOverdue(doc, today) {
  const d = doc || {};
  if (String(d.state) !== REFERENCE || !DATE_RE.test(String(d.noticedAt || '')) || !DATE_RE.test(String(today || ''))) return false;
  const days = (Date.parse(today + 'T00:00:00Z') - Date.parse(d.noticedAt + 'T00:00:00Z')) / 86400000;
  return days > NOTICE_FLAG_DAYS;
}
